import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "../../components/ui/button";
import { ReactComponent as RazorpayIcon } from "../../devdata/icons/razorpay.svg";
import {
  Menu,
  X,
  LogIn,
  Search,
  Zap,
  BarChart,
  Clock,
  TrendingUp,
  Target,
  ChevronDown,
  ExternalLink,
  BarChart2,
  Gauge,
  Compass,
  BrainCircuit,
  BookOpen,
  ArrowRight,
  Twitter,
  Facebook,
  Instagram,
  Youtube,
  Heart,
  ArrowRightLeft,
} from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import expandedLogo from "../../devdata/icons/marketpulse.svg";
import collapsedLogo from "../../devdata/icons/s_logo.svg";
import MomentumIcon from "../../components/landing/icons/MomentumIcon";
import LearningIcon from "../../components/landing/icons/LearningIcon";
import TabImageCard from "../../components/landing/TabImageCard";
import ContinuousBackground from "../../components/landing/ContinuousBackground";

// Define Header component explicitly for use in the landing page
const Header = () => {
  return <Navbar />;
};

export { Header };

const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, [breakpoint]);

  return isMobile;
};

export { useIsMobile };

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLoginClick = () => {
    navigate("/login");
  };

  const handleGetStartedClick = () => {
    navigate("/signup");
  };

  const navItems = [
    { name: "Plans & Pricing", path: "/Subscriptions" },
    { name: "Support", path: "/support" },
    { name: "About Us", path: "/AboutUs" },
    { name: "Privacy Policy", path: "/privacy" },
    { name: "FAQs", path: "/faqs" },
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${isScrolled ? "bg-black/80 backdrop-blur-md py-8" : "bg-transparent py-5"
        }`}
    >
      <div className="max-w-7xl mx-auto flex justify-between items-center px-4">
        <Link to="/" className="flex items-center gap-1">
          <img
            src={expandedLogo}
            alt="MarketPulse"
            className="h-14 lg:h-18 w-auto"
          />
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <ul className="flex gap-6">
            {navItems.map((item) => (
              <li key={item.name}>
                <Link
                  to={item.path}
                  className={`transition-colors ${isActive(item.path)
                    ? "text-market-teal font-medium"
                    : "text-white/80 hover:text-market-teal"
                    }`}
                >
                  {item.name}
                </Link>
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-3">
            <Button
              onClick={handleLoginClick}
              className="bg-market-teal/20 hover:bg-market-teal/30 text-market-teal border border-market-teal/50 font-semibold px-6 py-2 transition-all duration-300 hover:scale-105 shadow-md hover:shadow-market-teal/30"
              variant="ghost"
            >
              <LogIn className="mr-2 h-4 w-4" /> Login
            </Button>
            <Button
              onClick={handleGetStartedClick}
              variant="intellectia"
              className="text-black hover:scale-105 transition-transform duration-300"
            >
              Get Started
            </Button>
          </div>
        </div>

        <button
          className="md:hidden text-white"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {isMenuOpen && (
        <div className="md:hidden absolute top-full left-0 w-full bg-black/95 backdrop-blur-md py-5 animate-fade-in">
          <div className="container mx-auto px-4">
            <ul className="flex flex-col gap-4">
              {navItems.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.path}
                    className={`block py-2 transition-colors ${isActive(item.path)
                      ? "text-market-teal font-medium"
                      : "text-white/80 hover:text-market-teal"
                      }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="flex flex-col gap-3 mt-4">
              <Button
                onClick={() => {
                  handleLoginClick();
                  setIsMenuOpen(false);
                }}
                className="bg-market-teal/20 hover:bg-market-teal/30 text-market-teal border border-market-teal/50 w-full font-semibold transition-all duration-300 hover:scale-105 shadow-md hover:shadow-market-teal/30"
                variant="ghost"
              >
                <LogIn className="mr-2 h-4 w-4" /> Login
              </Button>
              <Button
                onClick={() => {
                  handleGetStartedClick();
                  setIsMenuOpen(false);
                }}
                variant="intellectia"
                className="text-black w-full hover:scale-105 transition-transform duration-300"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export { Navbar };

const HeroSection = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const handleGetStartedClick = () => {
    navigate("/signup");
  };

  return (
    <section className="relative pt-32 pb-10 overflow-hidden">
      <div className="container mx-auto px-4 py-8 md:py-16 relative z-10">
        <div className="max-w-4xl mx-auto text-center mb-8">
          <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 sm:mb-8 leading-tight tracking-tight animate-fade-in">
            Key to successful trading is following the{" "}
            <span className="text-market-teal">Money Flow</span>
          </h1>

          <p
            style={{ animationDelay: "0.6s" }}
            className="sm:text-xl md:text-2xl text-white/80 mb-8 sm:mb-10 animate-fade-in font-thin"
          >
            marketpulse.exchange helps you do exactly that — by identifying
            sector-based momentum in real time and revealing which stocks are
            ready to move.
          </p>

          <div
            className="flex flex-col sm:flex-row gap-5 justify-center animate-fade-in"
            style={{ animationDelay: "0.9s" }}
          >
            <Button
              onClick={handleGetStartedClick}
              variant="intellectia"
              className="px-8 py-4 md:py-6 text-lg shadow-md shadow-market-teal/20"
            >
              Get Early Access
            </Button>
          </div>
        </div>

        <div
          className="mt-10 mb-8 mx-auto max-w-6xl rounded-lg overflow-hidden shadow-2xl animate-fade-in"
          style={{ animationDelay: "1.2s" }}
        >
          <img
            src="/lovable-uploads/a6f311da-dcaf-439d-b522-9822a01bdcde.png"
            alt="MarketPulse Dashboard"
            className="w-full h-auto rounded-lg border border-market-teal/20"
          />
          <div className="text-center mt-4 text-white/60 text-xs">
            <p>
              Pi (Predictive Intelligence) — A real-time momentum scanner that
              helps traders identify intraday breakout opportunities in FnO
              stocks
            </p>
          </div>
        </div>

        <div className="absolute bottom-[-2rem] left-1/2 transform -translate-x-1/2 animate-bounce hidden md:block opacity-60">
          <ChevronDown className="text-market-teal" size={36} />
        </div>
      </div>

      <div className="container mx-auto mt-12 md:mt-16 px-4 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
          <div
            className="intellectia-card p-6 animate-fade-in"
            style={{ animationDelay: "1.4s" }}
          >
            <div className="flex items-center gap-3 mb-4">
              <Search className="text-market-teal" size={24} />
              <h3 className="text-2xl font-bold">Powered by Pi</h3>
            </div>
            <p className="text-white/80 mb-4">
              Pi - Predictive Intelligence ranks stocks based on intraday
              momentum, hit frequency, and sector strength.
            </p>
            <ul className="space-y-2 text-white/90">
              <li className="flex items-start gap-3">
                <BarChart
                  className="text-market-teal mt-1 shrink-0"
                  size={18}
                />
                <span>Live scan analysis every minute</span>
              </li>
              <li className="flex items-start gap-3">
                <TrendingUp
                  className="text-market-teal mt-1 shrink-0"
                  size={18}
                />
                <span>Sector-boosted momentum scores</span>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="text-market-teal mt-1 shrink-0" size={18} />
                <span>Clear "Good to Enter" trade Suggestions</span>
              </li>
            </ul>
          </div>

          <div
            className="intellectia-card p-6 animate-fade-in"
            style={{ animationDelay: "1.6s" }}
          >
            <div className="flex items-center gap-3 mb-4">
              <Zap className="text-market-teal" size={24} />
              <h3 className="text-2xl font-bold">Automate via Algotest</h3>
            </div>
            <p className="text-white/80 mb-4">
              Seamlessly execute trades with zero emotion. Our integration with
              Algotest enables one-click execution of:
            </p>
            <ul className="space-y-2 text-white/90">
              <li className="flex items-start gap-3">
                <TrendingUp
                  className="text-market-teal mt-1 shrink-0"
                  size={18}
                />
                <span>Entry & Exit</span>
              </li>
              <li className="flex items-start gap-3">
                <Target className="text-market-teal mt-1 shrink-0" size={18} />
                <span>Stop Loss & Target</span>
              </li>
              <li className="flex items-start gap-3">
                <BarChart
                  className="text-market-teal mt-1 shrink-0"
                  size={18}
                />
                <span>Trailing SL management</span>
              </li>
            </ul>
            <p className="mt-4 text-market-teal text-sm">
              Stay disciplined. Let the tech handle your trades.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const DashboardSection = () => {
  const images = {
    mood: "/lovable-uploads/bae5e810-9e81-4d6c-8769-a9986f98d53d.png",
    sector: "/lovable-uploads/369816c1-efbd-4538-9452-9cd8e6081646.png",
    scanner: "/lovable-uploads/a6f311da-dcaf-439d-b522-9822a01bdcde.png",
    performers: "/lovable-uploads/47fd05ba-81a5-4e35-bad4-ab233370bc55.png",
    momentum: "/lovable-uploads/e845483b-1247-4895-b58e-c03c01a549f3.png",
  };

  return (
    <section className="py-20 relative overflow-hidden bg-black">
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 animate-fade-in">
            See <span className="text-market-teal">MarketPulse</span> in Action
          </h2>
          <p
            style={{ animationDelay: "0.2s" }}
            className="text-white/80 max-w-3xl mx-auto animate-fade-in font-normal"
          >
            The key to successful trading is following the money flow.
            MarketPulse helps you do exactly that — by identifying sector-based
            momentum in real time.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Pi Scanner */}
          <div className="bg-black/50 rounded-lg p-4 border border-market-teal/20 hover:border-market-teal/40 transition-all">
            <div className="flex items-center gap-3 mb-4">
              <Gauge className="text-market-teal" size={24} />
              <h3 className="text-xl font-semibold">
                Pi Scanner - Predictive Intelligence
              </h3>
              <span className="ml-auto text-market-teal text-sm">
                Real-time
              </span>
            </div>
            <div className="rounded-lg overflow-hidden">
              <img
                src={images.scanner}
                alt="Pi Scanner"
                className="w-full h-auto"
              />
            </div>
          </div>

          {/* Sector Flow */}
          <div className="bg-black/50 rounded-lg p-4 border border-market-teal/20 hover:border-market-teal/40 transition-all">
            <div className="flex items-center gap-3 mb-4">
              <BarChart2 className="text-market-teal" size={24} />
              <h3 className="text-xl font-semibold">Sector Flow</h3>
            </div>
            <div className="rounded-lg overflow-hidden">
              <img
                src={images.sector}
                alt="Sector Analysis"
                className="w-full h-auto"
              />
            </div>
          </div>

          {/* Market Mood Index */}
          <div className="bg-black/50 rounded-lg p-4 border border-market-teal/20 hover:border-market-teal/40 transition-all">
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp className="text-market-teal" size={24} />
              <h3 className="text-xl font-semibold">Market Mood Index</h3>
              <span className="ml-auto text-market-teal text-sm">
                Updated just now
              </span>
            </div>
            <div className="rounded-lg overflow-hidden">
              <img
                src={images.mood}
                alt="Market Mood Index"
                className="w-full h-auto"
              />
            </div>
          </div>

          {/* Top Performers */}
          <div className="bg-black/50 rounded-lg p-4 border border-market-teal/20 hover:border-market-teal/40 transition-all">
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp className="text-market-teal" size={24} />
              <h3 className="text-xl font-semibold">Top Performers</h3>
            </div>
            <div className="rounded-lg overflow-hidden">
              <img
                src={images.performers}
                alt="Top Performers"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>

        {/* Momentum Analysis */}
        <div className="mt-8 pbg-black/50 rounded-lg p-4 border border-market-teal/20 hover:border-market-teal/40 transition-all">
          <div className="flex items-center gap-3 mb-4">
            <MomentumIcon className="text-market-teal" size={24} />
            <h3 className="text-xl font-semibold">Momentum Analysis</h3>
          </div>
          <div className="rounded-lg overflow-hidden">
            <img
              src={images.momentum}
              alt="Momentum Analysis"
              className="w-full h-auto"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const HowItWorks = () => {
  const isMobile = useIsMobile();

  const steps = [
    {
      icon: Compass,
      title: "Discover",
      description:
        "Sign up and explore the MarketPulse dashboard with real-time market data.",
    },
    {
      icon: BarChart2,
      title: "Analyze",
      description:
        "Use our powerful analytics tools to gain insights into market trends and opportunities.",
    },
    {
      icon: BrainCircuit,
      title: "Predict",
      description:
        "Leverage AI-powered predictions to anticipate market movements.",
    },
    {
      icon: Zap,
      title: "Execute & Automate",
      description:
        "Execute your trade with confidence - manually or with automation powered by Algotest",
    },
  ];

  return (
    <section
      id="how-it-works"
      className="py-20 relative overflow-hidden bg-black"
    >
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 animate-fade-in">
            How <span className="text-market-teal">MarketPulse</span> Works
          </h2>
          <p
            className="text-white/80 animate-fade-in"
            style={{ animationDelay: "0.2s" }}
          >
            A simple process to navigate complex financial markets.
          </p>
        </div>

        <div className="relative">
          {/* Desktop flowing neon line */}
          <div className="hidden md:block absolute top-1/2 left-[12.5%] w-3/4 h-[2px] bg-market-teal/10 -translate-y-1/2">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-market-teal to-transparent animate-flowingLine"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-16">
            {steps.map((step, index) => (
              <div
                key={index}
                className="relative flex flex-col items-center text-center animate-fade-in z-10"
                style={{ animationDelay: `${0.3 * (index + 1)}s` }}
              >
                <div className="flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-black to-gray-800 mb-6 border-2 border-market-teal/50 shadow-lg">
                  <step.icon
                    className={`h-10 w-10 ${index === 0
                      ? "text-market-teal"
                      : index === 1
                        ? "text-market-gold"
                        : index === 2
                          ? "text-market-blue"
                          : "text-orange-500"
                      }`}
                  />
                </div>
                <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                <p className="text-white/80 text-sm">{step.description}</p>

                {/* Mobile flowing neon lines */}
                {index < steps.length - 1 && (
                  <div className="md:hidden relative w-[2px] h-16 my-6 overflow-hidden">
                    <div className="absolute inset-0 bg-market-teal/10">
                      <div
                        className={`absolute inset-0 bg-gradient-to-b from-transparent via-market-teal to-transparent ${index === 0
                          ? "animate-flowingLine1"
                          : index === 1
                            ? "animate-flowingLine2"
                            : "animate-flowingLine3"
                          }`}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const MomentumSection = () => {
  const navigate = useNavigate();
  const handleStartNowClick = () => navigate("/signup");

  return (
    <section className="py-20 relative overflow-hidden bg-black">
      <div className="container mx-auto px-4 text-center relative z-10">
        <div className="flex flex-col md:flex-row items-center justify-center gap-3 mb-6 animate-fade-in">
          <MomentumIcon className="text-market-teal" size={48} />
          <h2 className="text-3xl md:text-4xl font-bold">
            Find Momentum Before Everyone Else
          </h2>
        </div>
        <p
          className="text-white/80 max-w-3xl mx-auto mb-10 animate-fade-in"
          style={{ animationDelay: "0.2s" }}
        >
          Scan 250+ F&O stocks in real time and get signals backed by technical
          and sectoral strength with our Pi Scanner. Whether it's an Opening
          Range Breakout or a PDH or PDL Retest – we've got you covered.
        </p>
        <Button
          onClick={handleStartNowClick}
          variant="intellectia"
          className="px-8 py-4 md:py-6 text-lg animate-fade-in"
          style={{ animationDelay: "0.4s" }}
        >
          Start Now
        </Button>
      </div>
    </section>
  );
};

const LearningSection = () => {

  return (
    <section className="py-20 relative overflow-hidden bg-[#0a0e17]">
      <div className="container mx-auto px-4 text-center relative z-10">
        <div className="flex flex-col md:flex-row items-center justify-center gap-3 mb-6 animate-fade-in">
          <LearningIcon className="text-market-teal" size={48} />
          <h2 className="text-3xl md:text-4xl font-bold">
            Learn. Improve. Repeat.
          </h2>
        </div>
        <p
          className="text-white/80 max-w-3xl mx-auto mb-10 animate-fade-in"
          style={{ animationDelay: "0.2s" }}
        >
          Get access to free, regularly updated videos that help you understand
          strategies, risk management, and platform usage.
        </p>
        <Link to="/signup">
          <Button
            variant="outline"
            className="border-white/20 text-white hover:bg-white/5 hover:border-market-teal/50 px-8 py-4 md:py-6 text-lg animate-fade-in"
            style={{ animationDelay: "0.4s" }}
          >
            Watch Learning Videos <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </Link>
      </div>
    </section>
  );
};

const CTASection = () => {
  const navigate = useNavigate();
  const handleGetStartedClick = () => navigate("/signup");

  return (
    <section className="py-24 relative overflow-hidden bg-black">
      <div className="absolute inset-0 bg-gradient-radial from-market-teal/5 via-transparent to-transparent opacity-70"></div>
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gradient animate-fade-in">
            Ready to Trade Smarter?
          </h2>
          <p
            className="text-xl text-white/80 mb-10 max-w-2xl mx-auto animate-fade-in"
            style={{ animationDelay: "0.2s" }}
          >
            Start using the Pi Scanner and Automate via Algotest today.
          </p>
          <Button
            onClick={handleGetStartedClick}
            variant="intellectia"
            className="px-8 py-4 md:py-6 text-lg rounded-md animate-fade-in"
            style={{ animationDelay: "0.4s" }}
          >
            Get Started Now
          </Button>
          <div
            className="mt-8 flex flex-col items-center animate-fade-in"
            style={{ animationDelay: "0.6s" }}
          >
            <p className="text-white/60 text-sm mb-4">
              Payments secured by Razorpay
            </p>
            <RazorpayIcon className="h-8 w-auto opacity-90 hover:opacity-100 transition-opacity  bg-white p-1 rounded-md" />
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  const legalLinks = [
    { name: "Refund Policy", link: "/refund" },
    { name: "Disclaimer", link: "/disclaimer" },
    { name: "About Us", link: "/AboutUs" },
    { name: "Privacy Policy", link: "/privacy" },
    { name: "Terms Of Service", link: "/terms" },
    { name: "Disclosure Of Material Information", link: "/disclosure" },
    // { name: "SEBI Certificate", link: "/sebi" },
  ];

  const smallLegalLinks = [
    { name: "Privacy Policy", link: "/privacy" },
    { name: "Terms Of Service", link: "/terms" },
    // { name: "Cookie Policy", link: "/cookie" },
  ];

  const socialIcons = [
    {
      icon: Twitter,
      href: "https://x.com/utkarshbhargv?lang=en",
      label: "Twitter",
    },
    // { icon: Facebook, href: "#", label: "Facebook" },
    {
      icon: Youtube,
      href: "https://www.youtube.com/@UtkarshBhargava",
      label: "YouTube",
    },
    {
      icon: Instagram,
      href: "https://www.instagram.com/utkarshbhargava_/",
      label: "Instagram",
    },
  ];

  return (
    <footer className="relative overflow-hidden border-t border-market-teal/20 pt-16 pb-8 bg-[#0a0e17]">
      {/* Footer top glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-px bg-market-teal/50 blur-sm"></div>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-1 bg-market-teal/20 blur-md"></div>

      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Brand section */}
          <div className="md:col-span-6 space-y-6">
            <Link to="/" className="inline-block">
              <img
                src={expandedLogo}
                alt="MarketPulse"
                className="h-10 sm:h-14 w-auto"
              />
            </Link>
            <p className="text-white/60 mb-6 max-w-xl">
              Discover, validate, and automate high-probability intraday trades
              with Pi – Predictive Intelligence.
            </p>
            <div className="flex gap-4">
              {socialIcons.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center border border-market-teal/30 hover:bg-market-teal/20 transition-colors"
                  aria-label={social.label}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <social.icon size={18} className="text-market-teal" />
                </a>
              ))}
            </div>
          </div>

          {/* Links Column */}
          <div className="md:col-span-5">
            <h3 className="text-white font-semibold mb-4">Legal & Company</h3>
            <div className="grid grid-cols-2 gap-4">
              {legalLinks
                .slice(0, Math.ceil(legalLinks.length / 2))
                .map((link, index) => (
                  <Link
                    key={index}
                    to={link.link}
                    className="text-white/60 hover:text-market-teal transition-colors"
                  >
                    {link.name}
                  </Link>
                ))}
              {legalLinks
                .slice(Math.ceil(legalLinks.length / 2))
                .map((link, index) => (
                  <Link
                    key={index + Math.ceil(legalLinks.length / 2)}
                    to={link.link}
                    className="text-white/60 hover:text-market-teal transition-colors"
                  >
                    {link.name}
                  </Link>
                ))}
            </div>
          </div>
        </div>

        {/* Copyright section */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="text-white/60 text-sm">
            © {new Date().getFullYear()} marketpulse.exchange - Built by
            Traders, For Traders.
          </p>

          <div className="flex flex-wrap gap-6 items-center">
            <div className="flex flex-wrap gap-6 items-center">
              <div className="text-white/60 text-sm flex items-center">
                Made In India With{" "}
                <Heart
                  size={14}
                  className="mx-1 text-market-teal fill-current"
                />
              </div>
            </div>
            {/* {smallLegalLinks.map((link, index) => (
              <Link
                key={index}
                to={link.link}
                className="text-white/60 hover:text-market-teal transition-colors"
              >
                {link.name}
              </Link>
            ))} */}
          </div>
        </div>
      </div>
    </footer>
  );
};

const Socials = () => {

  const socialIcons = [
    {
      icon: Twitter,
      href: "https://x.com/utkarshbhargv?lang=en",
      label: "Twitter",
    },
    // { icon: Facebook, href: "#", label: "Facebook" },
    {
      icon: Youtube,
      href: "https://www.youtube.com/@UtkarshBhargava",
      label: "YouTube",
    },
    {
      icon: Instagram,
      href: "https://www.instagram.com/utkarshbhargava_/",
      label: "Instagram",
    },
  ];
  return (
    <div className="flex gap-4 justify-center">
      {socialIcons.map((social, index) => (
        <a
          key={index}
          href={social.href}
          className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center border border-market-teal/30 hover:bg-market-teal/20 transition-colors"
          aria-label={social.label}
          target="_blank"
          rel="noopener noreferrer"
        >
          <social.icon size={18} className="text-market-teal" />
        </a>
      ))}
    </div>
  )
}

export { Footer, Socials };


const LandingPage = () => {
  return (
    <ContinuousBackground>
      <div>
        <Header />
        <HeroSection />
        <DashboardSection />
        <HowItWorks />
        <MomentumSection />
        <LearningSection />
        <CTASection />
        <Footer />
      </div>
    </ContinuousBackground>
  );
};

export default LandingPage;
